import { Button } from 'blaze-ui/Button'
import Styles from './PerformanceInsights.module.scss'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { FooterButtons } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { LinkAngled } from 'blaze-ui/icons'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { useConnectAccountPopup } from 'components/Onboarding/useConnectAccountPopup'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { InitiateOauthTrigger } from 'components/ConnectIntegrationTrigger/InitiateOauthTrigger'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { Icon } from 'blaze-ui/icons/Types'
import { organicChannels } from '../InsightChannelsSelection/utils'
import { uniqBy } from 'lodash'

export interface PerformanceInsightsEmptyProps {
  onBack: () => void
  onSkip: () => void
  brandKitId: number
  insightChannels: string[]
  onConnecting?: () => void
}

export const PerformanceInsightsEmpty = ({
  onBack,
  brandKitId,
  onSkip,
  insightChannels,
  onConnecting,
}: PerformanceInsightsEmptyProps) => {
  const { connect } = useConnectAccountPopup({
    showToast: false,
    onConnecting,
  })
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  const mainChannel =
    (insightChannels.includes(ExternalIntegrationEnum.Instagram) ||
    !organicChannels.some((channel) => insightChannels.includes(channel.name))
      ? organicChannels.find((channel) => channel.name === ExternalIntegrationEnum.Instagram)
      : organicChannels.find((channel) => channel.name === insightChannels[0])) || organicChannels[0]
  const otherChannels = uniqBy(
    [
      ...organicChannels.filter((channel) => insightChannels.includes(channel.name)),
      ...organicChannels.filter((channel) => mainChannel.name !== channel.name),
    ],
    'name',
  ).slice(1, 3)

  const onConnect = (popUp?: Window) => {
    connect(popUp, {
      onConnect: () => {
        queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', 'detail', brandKitId])
      },
    })
  }

  const onConnectionPress = () => {
    connect(undefined, {
      onConnect: () => {
        queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', 'detail', brandKitId])
      },
    })
  }

  return (
    <MultiStepStepLayout
      scrollContainerClassName={Styles.scrollContainer}
      title={`Connect ${mainChannel.title} for performance insights`}
      description="Link your account to track what's working and get personalized recommendations"
    >
      <StickyFooterProvider
        footer={
          <StickyFooter>
            <FooterButtons>
              <Button variant="tertiary" size="lg" onPress={onBack} aria-label="Back">
                Back
              </Button>
              <Button size="lg" variant="tertiary" onPress={onSkip} aria-label="Skip for now">
                Skip for now
              </Button>
            </FooterButtons>
          </StickyFooter>
        }
      >
        <div className={Styles.noIntegrationsContainer}>
          <div className={Styles.mainConnectBanner}>
            <div className={Styles.provider}>
              {mainChannel.icon && <mainChannel.icon size={35} />}
              <Heading level={3}>{mainChannel.title}</Heading>
            </div>

            <InitiateOauthTrigger
              provider={mainChannel.name as ExternalIntegrationEnum}
              popUp
              onInitiatedOauth={onConnect}
            >
              <Button size="xl" variant="primary" frontIcon={LinkAngled} onPress={onConnectionPress}>
                Connect
              </Button>
            </InitiateOauthTrigger>
          </div>
          <div className={Styles.differenceContainer}>
            <Heading level={4}>See the difference insights make</Heading>
            <div className={Styles.differenceContent}>
              <div className={Styles.differenceContentColumn}>
                <Heading level={5}>Without Learning Loop</Heading>
                <div className={Styles.differenceContentColumnItems}>
                  <div className={Styles.differenceContentColumnItem}>
                    <Heading level={4}>😕</Heading>
                    <Text variant="secondary" color="var(--dark-80)">
                      Guessing what works
                    </Text>
                  </div>
                  <div className={Styles.differenceContentColumnItem}>
                    <Heading level={4}>❓</Heading>
                    <Text variant="secondary" color="var(--dark-80)">
                      No performance data
                    </Text>
                  </div>
                  <div className={Styles.differenceContentColumnItem}>
                    <Heading level={4}>🤷</Heading>
                    <Text variant="secondary" color="var(--dark-80)">
                      Trial and error
                    </Text>
                  </div>
                </div>
              </div>
              <div className={Styles.differenceContentColumn}>
                <Heading level={5}>With Learning Loop</Heading>
                <div className={Styles.differenceContentColumnItems}>
                  <div className={Styles.differenceContentColumnItem}>
                    <Heading level={4}>✅</Heading>
                    <Text variant="secondary" color="var(--dark-80)">
                      Know what drives results
                    </Text>
                  </div>
                  <div className={Styles.differenceContentColumnItem}>
                    <Heading level={4}>✅</Heading>
                    <Text variant="secondary" color="var(--dark-80)">
                      Clear metrics & trends
                    </Text>
                  </div>
                  <div className={Styles.differenceContentColumnItem}>
                    <Heading level={4}>✅</Heading>
                    <Text variant="secondary" color="var(--dark-80)">
                      Specific recommendations
                    </Text>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {otherChannels.length > 0 && (
            <div className={Styles.connectMoreContainer}>
              <Text>Want to see insights for your other accounts? </Text>
              <div className={Styles.connectMoreButtons}>
                {otherChannels.map((channel) => (
                  <InitiateOauthTrigger
                    provider={channel.name as ExternalIntegrationEnum}
                    popUp
                    onInitiatedOauth={onConnect}
                    key={channel.name}
                  >
                    <Button size="lg" variant="secondary" frontIcon={channel.icon as Icon} onPress={onConnectionPress}>
                      Connect {channel.title}
                    </Button>
                  </InitiateOauthTrigger>
                ))}
              </div>
            </div>
          )}
        </div>
      </StickyFooterProvider>
    </MultiStepStepLayout>
  )
}
